import { useCallback } from "react";
import useQuery, { useMutate, useRevalidate } from "../../../../../api/useQuery";
import { useChannel } from "../../../../../api/ws/useChannel";
import { useDelete, usePost } from "../../../../../api/useCall";
import { useUser } from "../../../../../api/useUser";

export function useDashboardLocks(projectId: string) {
  const user = useUser();
  const revalidate = useRevalidate();
  const { body: locks } = useQuery("/projects/:projectId(uuid)/dashboards/locks", {
    routeParams: {
      projectId,
    },
    keepFresh: true,
  });

  useChannel(
    {
      key: "dashboard_locks",
    },
    () => revalidate("/projects/:projectId(uuid)/dashboards/locks", { projectId }),
    true,
    true,
  );

  const isLocked = useCallback(
    (dashboardId: string) => locks.some(lock => lock.dashboardId === dashboardId),
    [locks],
  );
  const isUserLocked = useCallback(
    (dashboardId: string) =>
      locks.some(lock => lock.dashboardId === dashboardId && lock.userId === user.id),
    [locks, user.id],
  );
  const lockedBy = useCallback(
    (dashboardId: string) => locks.find(lock => lock.dashboardId === dashboardId)?.userName ?? null,
    [locks],
  );

  return { locks, isLocked, isUserLocked, lockedBy };
}

export const useLockDashboardCallback = (projectId: string) => {
  const user = useUser();
  const createDashboardLock = usePost(
    "/projects/:projectId(uuid)/dashboards/:dashboardId(uuid)/locks",
    false,
  );
  const mutateDashboardLocks = useMutate("/projects/:projectId(uuid)/dashboards/locks", {
    routeParams: {
      projectId,
    },
  });

  return useCallback(
    async (dashboardId: string) => {
      const rollback = mutateDashboardLocks(previous => ({
        ...previous,
        body: [
          ...previous.body.filter(lock => lock.dashboardId !== dashboardId),
          {
            dashboardId,
            userId: user.id,
            userName: user.name,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ],
      }));
      try {
        await createDashboardLock({
          routeParams: {
            projectId,
            dashboardId,
          },
        });
        return true;
      } catch (_) {
        rollback();
        return false;
      }
    },
    [user.id, user.name, createDashboardLock, mutateDashboardLocks, projectId],
  );
};

export function useUnlockDashboardCallback(projectId: string) {
  const deleteDashboardLock = useDelete(
    "/projects/:projectId(uuid)/dashboards/:dashboardId(uuid)/locks",
    false,
  );
  const mutateDashboardLocks = useMutate("/projects/:projectId(uuid)/dashboards/locks", {
    routeParams: {
      projectId,
    },
  });

  return useCallback(
    async (dashboardId: string) => {
      const rollback = mutateDashboardLocks(previous => ({
        ...previous,
        body: previous.body.filter(lock => lock.dashboardId !== dashboardId),
      }));
      try {
        await deleteDashboardLock({
          routeParams: {
            projectId,
            dashboardId,
          },
        }).catch(e => e);
        return true;
      } catch (_) {
        rollback();
        return false;
      }
    },
    [deleteDashboardLock, mutateDashboardLocks, projectId],
  );
}
